import { Link, useRouterState, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useRef } from "react";
import {
  Menu,
  X,
  Globe,
  ChevronDown,
  User,
  LogIn,
  LogOut,
  Heart,
} from "lucide-react";
import { useI18n } from "@project/lib/use-i18n";
import { smoothScrollToTop } from "@project/lib/scroll-to-top";
import logo from "@project/assets/jivandhara-logo.png";
import { useAuth } from "@project/hooks/useAuth";

const primaryNav = [
  { to: "/", key: "home" as const },
  { to: "/about", key: "about" as const },
  { to: "/donate", key: "donation" as const },
  { to: "/bhojanshala", key: "bhojanshala" as const },
  { to: "/gaushala", key: "gaushala" as const },
  { to: "/contact", key: "contact" as const },
];

const moreNav = [
  { to: "/events", key: "events" as const },
  // Hidden menu item
  // { to: "/apply-admission", key: "applyAdmission" as const },
  { to: "/resident-care", key: "residentCare" as const },
  { to: "/elder-care-program", key: "elderCareProgram" as const },
  { to: "/healthcare", key: "healthcare" as const },
  { to: "/gruh-udhyog", key: "gruhUdhyog" as const },
  { to: "/transparency", key: "transparency" as const },
  { to: "/tiffin-seva", key: "tiffinSeva" as const },
  { to: "/news", key: "news" as const },
  { to: "/trustees", key: "trustees" as const },
  { to: "/gallery", key: "gallery" as const },
  { to: "/visitor-inquiry", key: "visitorForm" as const },
  { to: "/jain-panchang", key: "jainPanchang" as const },
  { to: "/feedback", key: "feedback" as const },
];

export function SiteHeader() {
  const { t, lang, setLang } = useI18n();
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: s => s.location.pathname });
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
      if (
        profileRef.current &&
        !profileRef.current.contains(e.target as Node)
      ) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Lock background scroll while the mobile menu is open.
  useEffect(() => {
    if (!open) {
      return;
    }
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const handleLogoClick = () => {
    setOpen(false);
    if (pathname === "/") {
      smoothScrollToTop();
    }
  };

  return (
    <header
      className={`border-border/60 sticky top-0 z-50 border-b bg-background backdrop-blur transition-shadow duration-300 ${scrolled ? "shadow-sm" : ""}`}
    >
      {/* Tricolor heritage strip */}
      <div className="bg-tricolor h-1" aria-hidden />
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 lg:py-4">
        {/* Logo + Name */}
        <Link
          to="/"
          className="group flex shrink-0 items-center gap-3"
          onClick={handleLogoClick}
        >
          <div className="ring-gold/40 grid h-11 w-11 place-items-center overflow-hidden rounded-xl bg-secondary ring-1 transition-transform group-hover:scale-105">
            <img
              src={logo}
              alt="Jivandhara logo"
              className="h-10 w-10 object-contain"
            />
          </div>
          <div className="leading-tight">
            <div className="text-[15px] font-bold text-foreground">
              {t("brand.title")}
            </div>
            <div className="text-[11px] font-medium text-muted-foreground">
              {t("brand.subtitle")}
            </div>
          </div>
        </Link>

        {/* Primary nav */}
        <nav className="hidden items-center gap-1 lg:flex">
          {primaryNav.map(n => (
            <Link
              key={n.to}
              to={n.to}
              activeProps={{ className: "text-primary" }}
              activeOptions={{ exact: n.to === "/" }}
              className="text-foreground/80 rounded-full px-4 py-2 text-sm font-medium transition hover:text-primary"
            >
              {t(`nav.${n.key}`)}
            </Link>
          ))}
          {/* More dropdown */}
          <div className="relative" ref={moreRef}>
            <button
              onClick={() => setMoreOpen(v => !v)}
              className="text-foreground/80 flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium transition hover:text-primary"
            >
              {t("nav.more")}{" "}
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform ${moreOpen ? "rotate-180" : ""}`}
              />
            </button>
            {moreOpen && (
              <div className="shadow-foreground/5 animate-in fade-in slide-in-from-top-1 absolute right-0 top-full mt-2 w-56 rounded-xl border border-border bg-card p-2 shadow-lg duration-150">
                {moreNav.map(n => (
                  <Link
                    key={n.to}
                    to={n.to}
                    onClick={() => setMoreOpen(false)}
                    className="text-foreground/80 block rounded-lg px-3 py-2 text-sm transition hover:bg-secondary hover:text-primary"
                  >
                    {t(`menu.${n.key}`)}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>

        {/* Right cluster */}
        <div className="hidden items-center gap-2 lg:flex">
          {/* Language pill */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen(v => !v)}
              className="text-foreground/80 hover:border-primary/40 flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-2 text-sm font-medium transition hover:text-primary"
            >
              <Globe className="h-4 w-4" /> {lang === "en" ? "EN" : "ગુ"}{" "}
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {langOpen && (
              <div className="animate-in fade-in slide-in-from-top-1 absolute right-0 top-full mt-2 w-32 rounded-xl border border-border bg-card p-1 shadow-lg duration-150">
                <button
                  onClick={() => {
                    setLang("en");
                    setLangOpen(false);
                  }}
                  className={`block w-full rounded-lg px-3 py-2 text-left text-sm transition hover:bg-secondary ${lang === "en" ? "font-semibold text-primary" : ""}`}
                >
                  {t("lang.english")}
                </button>
                <button
                  onClick={() => {
                    setLang("gu");
                    setLangOpen(false);
                  }}
                  className={`block w-full rounded-lg px-3 py-2 text-left text-sm transition hover:bg-secondary ${lang === "gu" ? "font-semibold text-primary" : ""}`}
                >
                  {t("lang.gujarati")}
                </button>
              </div>
            )}
          </div>

          {/* Auth State Button / Profile Dropdown */}
          {!isAuthenticated ? (
            <Link
              to="/login"
              className="text-foreground/80 hover:border-primary/40 flex items-center gap-1.5 rounded-full border border-border bg-background px-4 py-2 text-sm font-medium transition hover:text-primary"
            >
              <LogIn className="h-4 w-4 text-primary" /> Login
            </Link>
          ) : (
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setProfileOpen(v => !v)}
                className="border-primary/30 bg-primary/10 hover:bg-primary/20 flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold text-primary transition"
              >
                <div className="grid h-7 w-7 place-items-center rounded-full bg-primary text-primary-foreground">
                  <User className="h-4 w-4" />
                </div>
                <span className="max-w-[100px] truncate">
                  {user?.first_name ?? user?.username ?? "Donor"}
                </span>
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform ${profileOpen ? "rotate-180" : ""}`}
                />
              </button>

              {profileOpen && (
                <div className="animate-in fade-in slide-in-from-top-1 absolute right-0 top-full mt-2 w-48 rounded-xl border border-border bg-card p-1.5 shadow-lg duration-150">
                  <div className="border-border/60 border-b px-3 py-2">
                    <p className="truncate text-xs font-semibold text-foreground">
                      {user?.first_name
                        ? `${user.first_name} ${user.last_name ?? ""}`
                        : "Donor Profile"}
                    </p>
                    <p className="truncate text-[11px] text-muted-foreground">
                      {user?.email ?? user?.phone_number}
                    </p>
                  </div>

                  <Link
                    to="/account"
                    onClick={() => setProfileOpen(false)}
                    className="text-foreground/80 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition hover:bg-secondary hover:text-primary"
                  >
                    <User className="h-4 w-4" /> My Profile
                  </Link>

                  <Link
                    to="/account"
                    onClick={() => setProfileOpen(false)}
                    className="text-foreground/80 flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition hover:bg-secondary hover:text-primary"
                  >
                    <Heart className="h-4 w-4" /> My Donations
                  </Link>

                  <button
                    onClick={() => {
                      setProfileOpen(false);
                      logout();
                      navigate({ to: "/" });
                    }}
                    className="hover:bg-destructive/10 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-destructive transition"
                  >
                    <LogOut className="h-4 w-4" /> Logout
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile controls: language toggle + menu */}
        <div className="flex items-center gap-2 lg:hidden">
          <button
            onClick={() => setLang(lang === "en" ? "gu" : "en")}
            className="text-foreground/80 hover:border-primary/40 flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-sm font-medium transition hover:text-primary"
            aria-label={t("lang.change")}
          >
            <Globe className="h-4 w-4" /> {lang === "en" ? "EN" : "ગુ"}
          </button>
          <button
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            className="p-1"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="animate-in fade-in slide-in-from-top-2 max-h-[calc(100dvh-4.5rem)] overflow-y-auto overscroll-contain border-t border-border bg-background duration-200 lg:hidden">
          <div className="flex flex-col p-4">
            {[
              ...primaryNav.map(n => ({ to: n.to, label: t(`nav.${n.key}`) })),
              ...moreNav.map(n => ({ to: n.to, label: t(`menu.${n.key}`) })),
            ].map(n => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="py-2 text-sm transition-colors hover:text-primary"
              >
                {n.label}
              </Link>
            ))}

            {isAuthenticated ? (
              <div className="mt-4 flex flex-col gap-2 border-t border-border pt-4">
                <Link
                  to="/account"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 py-2 text-sm font-semibold text-primary"
                >
                  <User className="h-4 w-4" /> My Profile / Donations
                </Link>
                <button
                  onClick={() => {
                    setOpen(false);
                    logout();
                    navigate({ to: "/" });
                  }}
                  className="flex items-center gap-2 py-2 text-sm font-medium text-destructive"
                >
                  <LogOut className="h-4 w-4" /> Logout
                </button>
              </div>
            ) : (
              <div className="mt-4 border-t border-border pt-4">
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm"
                >
                  <LogIn className="h-4 w-4" /> Login
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
